import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/api";

export default function Login() {
  const { role } = useParams(); // 'owner' | 'tenant'
  const isTenant = role === "tenant";
  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
    else if (user && user.role === role) navigate(`/${role}/dashboard`, { replace: true });
  }, [user, role, navigate]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        if (u.role === "admin") {
          toast.success(`Welcome back, ${u.name}!`);
          navigate("/admin");
          return;
        }
        if (u.role !== role) {
          toast.error(`This account is registered as ${u.role}. Please use the ${u.role} login.`);
          return;
        }
        toast.success(`Welcome back, ${u.name}!`);
        navigate(`/${role}/dashboard`);
      } else {
        const u = await register({ ...form, role });
        toast.success(`Account created. Welcome, ${u.name}!`);
        navigate(`/${role}/dashboard`);
      }
    } catch (err) {
      toast.error(err.message || "Authentication failed");
    } finally { setBusy(false); }
  };

  const demoEmail = isTenant ? "tenant@demo.in" : "owner@demo.in";
  const demoPassword = isTenant ? "Tenant@12345" : "Owner@12345";
  const fillDemo = () => setForm({ ...form, email: demoEmail, password: demoPassword });

  return (
    <div data-testid={`page-login-${role}`} className="min-h-[calc(100vh-160px)] grid lg:grid-cols-2">
      <aside className="hidden lg:flex flex-col justify-between bg-brand-navy text-white p-12">
        <div>
          <img src={LOGO_URL} alt="Hi-Tech Realty" className="h-14 w-14 bg-white p-1 object-contain" />
          <div className="mt-8 eyebrow !text-brand-cyan">{isTenant ? "Tenant Service Portal" : "My Property Care Dashboard"}</div>
          <h1 className="mt-3 font-display text-4xl xl:text-5xl font-bold leading-tight">
            {isTenant ? "Raise an issue. Track the update. Keep your rental experience smooth." : "Verified updates. Geo-tagged proof. Documented care."}
          </h1>
          <p className="mt-5 text-white/80 max-w-md">
            {isTenant
              ? "Pay rent, raise maintenance tickets, upload issue photos and stay in touch with your property manager — all in one place."
              : "Your single source of truth for property visits, rental statements, maintenance tickets, documents and reminders."}
          </p>
        </div>
        <div className="text-xs text-white/60">Your Property. Our Responsibility.</div>
      </aside>

      <section className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="eyebrow">{isTenant ? "Tenant Login" : "Owner Login"}</div>
          <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy">
            {mode === "login" ? "Welcome back." : "Create your account."}
          </h2>
          <div className="mt-2 text-sm text-brand-slate/70">
            {mode === "login" ? "Sign in to continue to your dashboard." : "Just a few details and you're in."}
          </div>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid={`${role}-login-form`}>
            {mode === "register" && (
              <>
                <Field label="Full Name" name="name" value={form.name} onChange={change} testid="auth-name" />
                <Field label="Phone (optional)" name="phone" value={form.phone} onChange={change} required={false} testid="auth-phone" />
              </>
            )}
            <Field label="Email" name="email" type="email" value={form.email} onChange={change} testid="auth-email" />
            <Field label="Password" name="password" type="password" value={form.password} onChange={change} testid="auth-password" />
            <button data-testid={`${role}-auth-submit`} disabled={busy} className="btn-cyan w-full disabled:opacity-60">
              {busy ? "Please wait…" : mode === "login" ? "Login" : "Create Account"}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between text-xs">
            <button
              data-testid={`${role}-toggle-mode`}
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-brand-navy hover:text-brand-cyan font-semibold uppercase tracking-[0.18em]"
            >
              {mode === "login" ? "Need an account? Register" : "Have an account? Login"}
            </button>
            <button
              data-testid={`${role}-fill-demo`}
              onClick={fillDemo}
              className="text-brand-cyan hover:underline font-semibold uppercase tracking-[0.18em]"
            >
              Use Demo Credentials
            </button>
          </div>

          <div className="mt-8 border-t border-brand-border pt-5 text-xs text-brand-slate/60">
            {isTenant ? (
              <>Are you the property owner? <Link to="/login/owner" className="text-brand-cyan font-semibold">Owner Login →</Link></>
            ) : (
              <>Are you a tenant? <Link to="/login/tenant" className="text-brand-cyan font-semibold">Tenant Login →</Link></>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, testid, type="text", required=true }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <input data-testid={testid} required={required} type={type} name={name} value={value} onChange={onChange}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" />
    </label>
  );
}
